import { inject, Injectable, signal } from '@angular/core';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { map, tap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

export type OnboardingExperienceLevel = 'beginner' | 'intermediate' | 'expert';

export type OnboardingRole =
  | 'businessOwner'
  | 'accountant'
  | 'technician'
  | 'trainee'
  | null;

interface OnboardingProfileDto {
  /** قادم من الـ Backend ضمن /User/Profile (إن وُجد) */
  isOnboardingCompleted?: boolean;
  onboardingRole?: OnboardingRole;
  onboardingExperienceLevel?: OnboardingExperienceLevel;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly auth = inject(AuthService);

  /** هل تم تحميل بيانات البروفايل الخاصة بالـ Onboarding من الـ Backend. */
  readonly profileLoaded = signal(false);
  readonly loading = signal(false);

  /** الحالة الفعلية للـ Onboarding كما يراها الـ Frontend. */
  readonly isOnboardingCompleted = signal<boolean>(true);
  readonly role = signal<OnboardingRole>(null);
  readonly experienceLevel = signal<OnboardingExperienceLevel>('intermediate');

  /** تحميل حالة الـ Onboarding من الـ Backend مرة واحدة فقط لكل جلسة. */
  ensureProfileLoaded(): Observable<boolean> {
    if (this.profileLoaded()) {
      return of(true);
    }

    this.loading.set(true);

    return this.auth.getUserProfile().pipe(
      map((response: any) => {
        const dto = (response ?? {}) as OnboardingProfileDto;

        // إذا لم يرسل الـ Backend قيمة، نعتبره مكتمل حتى لا نمنع المستخدم من النظام.
        const completed = dto.isOnboardingCompleted ?? true;
        this.isOnboardingCompleted.set(completed);

        if (dto.onboardingRole) {
          this.role.set(dto.onboardingRole);
        }

        if (dto.onboardingExperienceLevel) {
          this.experienceLevel.set(dto.onboardingExperienceLevel);
        }

        this.profileLoaded.set(true);
        this.loading.set(false);
        return true;
      }),
      catchError(() => {
        // في حالة الفشل نسمح بالدخول ولا نعلّق المستخدم في شاشة الإعداد.
        this.isOnboardingCompleted.set(true);
        this.profileLoaded.set(true);
        this.loading.set(false);
        return of(true);
      })
    );
  }

  /**
   * إكمال المعالج محلياً فقط (لا استدعاء Backend — معالج الإعداد كله في الواجهة).
   */
  markCompleted(): Observable<void> {
    return of(void 0).pipe(
      tap(() => {
        this.isOnboardingCompleted.set(true);
        this.profileLoaded.set(true);
        this.loading.set(false);
      })
    );
  }

  /** واجهة بسيطة للـ Guards لقراءة الحالة الحالية. */
  isCompleted(): boolean {
    return this.isOnboardingCompleted();
  }
}

